import { observable, makeObservable, computed, action } from 'mobx';
import {
  waiting,
  State,
  UserGroupMembershipsError,
  loadError,
  ready,
  ReadyArgs,
  creating,
  newFormSubmissionError,
  BulkGroupMembershipsResource,
  succeeded,
  UserGroupMembershipsNewFormResource,
} from './Types';
import ParticipantsStore from '../../../../../../../ParticipantsStore';
import { Maybe, just, nothing } from 'maybeasy';
import { FlashAlert, errorAlert } from '../../../../../../../Notifications/Types';
import { assertNever } from '@kofno/piper';
import { FormTemplate } from '../../../../../../../Resource/FormTemplates/Types';
import { Link } from '../../../../../../../Resource/Types';

class UserGroupMembershipsStore {
  @observable private _state: State = waiting();

  readonly participantsStore: ParticipantsStore;

  constructor(participantsStore: ParticipantsStore) {
    makeObservable(this);
    this.participantsStore = participantsStore;
  }

  @computed
  get state(): State {
    switch (this._state.kind) {
      case 'waiting':
        return this.participantsStore.state.kind === 'ready'
          ? { kind: 'loading-for-new' }
          : this._state;
      case 'loading-for-new':
      case 'load-error':
      case 'new-form-submission-error':
      case 'ready':
      case 'creating':
      case 'succeeded':
      case 'error':
        return this._state;
      default:
        return assertNever(this._state);
    }
  }

  @action
  ready = (args: ReadyArgs) => {
    switch (this.state.kind) {
      case 'loading-for-new':
        this._state = ready(args);
        break;
      case 'ready':
      case 'load-error':
      case 'new-form-submission-error':
      case 'error':
      case 'creating':
      case 'succeeded':
      case 'waiting':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  saving = (selectedUserGroupId: string) => {
    switch (this.state.kind) {
      case 'ready':
        const form = this.state.userGroupMembershipsFormResource;
        this._state = creating(form, this.state, selectedUserGroupId);
        break;
      case 'loading-for-new':
      case 'new-form-submission-error':
      case 'load-error':
      case 'error':
      case 'creating':
      case 'succeeded':
      case 'waiting':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadError = (error: UserGroupMembershipsError) => {
    switch (this.state.kind) {
      case 'loading-for-new':
        this._state = loadError(error);
        break;
      case 'ready':
      case 'load-error':
      case 'error':
      case 'creating':
      case 'succeeded':
      case 'new-form-submission-error':
      case 'waiting':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  newFormSubmissionError = (
    resource: UserGroupMembershipsNewFormResource,
    error: UserGroupMembershipsError,
    template: FormTemplate,
  ) => {
    switch (this.state.kind) {
      case 'creating':
        this._state = newFormSubmissionError(resource, error, template);
        break;
      case 'ready':
      case 'load-error':
      case 'error':
      case 'loading-for-new':
      case 'new-form-submission-error':
      case 'succeeded':
      case 'waiting':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  succeeded = (resource: BulkGroupMembershipsResource) => {
    switch (this.state.kind) {
      case 'creating':
        this._state = succeeded(resource);
        break;
      case 'ready':
      case 'load-error':
      case 'error':
      case 'loading-for-new':
      case 'new-form-submission-error':
      case 'succeeded':
      case 'waiting':
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get notification(): Maybe<FlashAlert> {
    switch (this.state.kind) {
      case 'error':
        return just(this.state).map(errorAlert);
      case 'new-form-submission-error':
      case 'load-error':
      case 'ready':
      case 'loading-for-new':
      case 'creating':
      case 'succeeded':
      case 'waiting':
        return nothing();
    }
  }

  @computed
  get template(): Maybe<FormTemplate> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading-for-new':
      case 'new-form-submission-error':
      case 'load-error':
      case 'creating':
      case 'succeeded':
      case 'error':
        return nothing();
      case 'ready':
        return just(this.state.template);
    }
  }

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading-for-new':
      case 'new-form-submission-error':
      case 'load-error':
      case 'succeeded':
      case 'error':
        return [];
      case 'ready':
      case 'creating':
        return this.state.userGroupMembershipsFormResource.links;
    }
  }
}

export default UserGroupMembershipsStore;
